import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
  useState,
} from "react"
import { Button } from "./button"
import { Close } from "./iconOutline"
import { type SolidIcon } from "./iconCore"

export const variants = [
  "neutral",
  "info",
  "highlight",
  "error",
  "warning",
  "success",
] as const

export type BannerProps = Omit<ComponentPropsWithoutRef<"div">, "children"> & {
  /**
   * The icon to display in the FeaturedIcon component. Note that this is provided
   * as a prop intentionally to restrict the range of sizes that can be used.
   */
  icon: SolidIcon

  /**
   * Represents the main announcement in the banner. In many cases this is all that
   * is necessary to notify the user about what happened.
   *
   * Note that this is recommended to be no longer than one line.
   */
  bannerTitle: ReactNode

  /**
   * Optionally provide a description if the title does not provide
   * sufficient information. Ideally this should also be short and easy to digest
   * but unlike the title it is acceptable for this to be several short sentences.
   */
  bannerDescription?: ReactNode

  /**
   * Optionally provide an action (or actions) that the user can take.
   */
  bannerAction?: ReactNode

  /**
   * The notification type, example "error", "warning", etc.
   *
   * @default neutral
   */
  "data-variant"?: (typeof variants)[number]

  /**
   * Whether the banner can be closed.
   *
   * @default false
   */
  closeable?: boolean

  /**
   * Optional callback for when the banner is closed, if closeable.
   */
  onClose?: () => void
}

export const Banner = forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      className,
      bannerTitle,
      bannerDescription,
      bannerAction,
      icon: Icon,
      closeable,
      onClose,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(true)

    if (!open) {
      return null
    }

    return (
      <div
        className={`banner ${className ?? ""}`}
        ref={ref}
        data-has-description={!!bannerDescription}
        {...props}
      >
        <div className="banner-icon">
          <Icon data-size="sm" />
        </div>
        <div className="banner-content">
          <p className="banner-title title-sm">{bannerTitle}</p>
          {bannerDescription && (
            <p className="banner-description">{bannerDescription}</p>
          )}
        </div>
        {bannerAction && <div className="banner-actions">{bannerAction}</div>}
        {closeable && (
          <Button
            iconOnly
            data-variant="tertiary-gray"
            aria-label="Close"
            onClick={() => {
              onClose?.()
              setOpen(false)
            }}
            size="sm"
            className="banner-close-button"
            icon={Close}
          />
        )}
      </div>
    )
  },
)

Banner.displayName = "Banner"
